import { InjectionToken, ModuleWithProviders, NgModule, Provider } from '@angular/core';
import { Observable } from 'rxjs';
import { FeatureFlaggingService } from './feature-flagging.service';
import { FeatureFlaggingDirective } from './feature-flagging.directive';
import { UserAccount } from '../../features/account/models/UserAccount';

export const ACCOUNT_INFO_TOKEN = new InjectionToken<Observable<UserAccount>>('ACCOUNT_INFO_TOKEN');
export const API_KEY_TOKEN = new InjectionToken<Observable<string>>('API_KEY_TOKEN');

interface LaunchDarklyKeyStep {
    withLaunchDarklyKey<T>(apiKeyFactory: (service: T) => Observable<string>, environmentDependency: any): AccountInfoStep;
}

interface AccountInfoStep {
    withAccountInfo<T>(accountInfoFactory: (service: T) => Observable<UserAccount>, storeDependency: any): FinalStep;
}

interface FinalStep {
    build(): ModuleWithProviders<FeatureFlaggingModule>;
}

@NgModule({
    declarations: [],
    imports: [],
    exports: []
})
export class FeatureFlaggingModule {
    /**
     * Builder to configure the FeatureFlaggingModule.
     *
     * @see `FeatureFlaggingModule`
     *
     * @example
     * FeatureFlaggingModule.build(builder => builder
        .withLaunchDarklyKey<EnvironmentService<ISellerXConfiguration>>(env => env.configuration$.pipe(map(config => config.launchDarklyKey)), EnvironmentService)
        .withAccountInfo<Store>(store => store.pipe(select(AccountLoginSelectors.getAccountLogin)), Store)
       )
     *
     */
    static build(builderDelegate: (moduleBuilder: LaunchDarklyKeyStep) => FinalStep): ModuleWithProviders<FeatureFlaggingModule> {
        const builder = new FeatureFlaggingModuleBuilder();
        return builderDelegate(builder).build();
    }
}

class FeatureFlaggingModuleBuilder implements LaunchDarklyKeyStep, AccountInfoStep, FinalStep {
    private readonly _providers: Provider[] = [];
    private _hasLaunchDarklyKey = false;
    private _hasAccountInfo = false;

    /**
     * Gets the list of providers configured by the builder.
     */
    private _getProviders() {
        return this._providers;
    }

    /**
     * Sets the api key that we use to connect to our current feature flagging service - LaunchDarkly.
     */
    public withLaunchDarklyKey<T>(apiKeyFactory: (service: T) => Observable<string>, environmentDependency: any): AccountInfoStep {
        this._providers.push({ provide: API_KEY_TOKEN, useFactory: apiKeyFactory, deps: [environmentDependency] });
        this._hasLaunchDarklyKey = true;
        return this;
    }

    /**
     * Sets the account login info so we can identify users within our current feature flagging service - LaunchDarkly.
     */
    public withAccountInfo<T>(accountInfoFactory: (service: T) => Observable<UserAccount>, storeDependency: any): FinalStep {
        this._providers.push({ provide: ACCOUNT_INFO_TOKEN, useFactory: accountInfoFactory, deps: [storeDependency] });
        this._hasAccountInfo = true;
        return this;
    }

    build(): ModuleWithProviders<FeatureFlaggingModule> {
        if (!this._hasLaunchDarklyKey) {
            throw new Error('FeatureFlaggingModule.withLaunchDarklyKey must be called.');
        }
        if (!this._hasAccountInfo) {
            throw new Error('FeatureFlaggingModule.withAccountInfo must be called.');
        }

        return {
            ngModule: FeatureFlaggingModule,
            providers: [
                ...this._getProviders(),
                FeatureFlaggingService
            ],
        };
    }
}
